import React from "react";
import { Box, Typography } from "@mui/material";
import { COLORS } from "../../common/colors";
import type { MedicationOption } from "src/providers/categoryMetadata";
import type { MedicationSeries } from "src/providers/compliance/complianceSidebar";
import { namedSeriesForDisplay } from "src/providers/compliance/complianceSidebar";
import {
  AUTOLOG_ONLY_TEXT,
  accountedPct
} from "src/providers/compliance/complianceSidebar";
import { CHART } from "./complianceColors";
import { ChartSwatch, DailyBarChart, integerTicks } from "./DailyBarChart";
import { formatDay } from "./dayTimeline";

interface MedicationBarChartsProps {
  series: MedicationSeries[];
  dates: string[];
  todayDate: string;
  studyMedications: string[];
  catalog: MedicationOption[];
}

// Taken is hatched so it differs from skipped by more than colour.
const TAKEN_STYLE = {
  color: CHART.TAKEN_FILL,
  stroke: CHART.TAKEN_STROKE,
  hatch: CHART.TAKEN_STROKE
};
const SKIPPED_STYLE = {
  color: CHART.SKIPPED_FILL,
  stroke: CHART.SKIPPED_STROKE
};

const SMALL_TEXT_SX = {
  fontFamily: "Work Sans, sans-serif",
  color: COLORS.GREY_900
};

function Legend(): JSX.Element {
  const items = [
    { label: "Taken", ...TAKEN_STYLE },
    { label: "Skipped", ...SKIPPED_STYLE }
  ];
  return (
    <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
      {items.map(({ label, ...style }) => (
        <Box
          key={label}
          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        >
          <ChartSwatch {...style} />
          <Typography sx={{ ...SMALL_TEXT_SX, fontSize: 12 }}>
            {label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export function MedicationBarCharts({
  series,
  dates,
  todayDate,
  studyMedications,
  catalog
}: MedicationBarChartsProps): JSX.Element {
  const named = namedSeriesForDisplay(series, studyMedications, catalog);

  return (
    <Box>
      {named.map(({ series: s, name }, idx) => {
        if (s.autologOnly) {
          return (
            <Box key={s.itemId} sx={{ mb: idx < named.length - 1 ? 2 : 0 }}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  fontFamily: "Work Sans, sans-serif",
                  color: COLORS.GREY_900,
                  mb: 0.5
                }}
              >
                {name}
              </Typography>
              <Typography sx={{ ...SMALL_TEXT_SX, fontSize: 12 }}>
                {AUTOLOG_ONLY_TEXT}
              </Typography>
            </Box>
          );
        }

        const result = accountedPct(s.days);
        const pct = result?.pct ?? 0;

        const maxScheduled = Math.max(...s.days.map((d) => d.scheduled), 1);
        const maxAccounted = Math.max(...s.days.map((d) => d.accounted), 1);
        const yMax = Math.max(maxScheduled, maxAccounted, 1);

        const yTicks = integerTicks(yMax);

        const bars = s.days.map((d) => {
          const label = formatDay(d.date);
          // Every day gets its own tooltip, so a day with nothing scheduled isn't read as missed.
          const tooltip = {
            in_progress: `${label}: in progress`,
            no_schedule: `${label}: no doses scheduled`,
            autolog_only: `${label}: ${AUTOLOG_ONLY_TEXT.toLowerCase()}`,
            scheduled: `${label}: ${d.accounted}/${d.scheduled} accounted for · ${d.taken} taken, ${d.skipped} skipped`
          }[d.status];

          const takenShown = Math.min(d.taken, d.scheduled);
          const skippedShown = Math.min(d.skipped, d.scheduled - takenShown);

          const segments = [];
          if (skippedShown > 0) {
            segments.push({
              value: skippedShown,
              ...SKIPPED_STYLE
            });
          }
          if (takenShown > 0) {
            segments.push({
              value: takenShown,
              ...TAKEN_STYLE
            });
          }

          return { date: d.date, segments, tooltip };
        });

        return (
          <Box key={s.itemId} sx={{ mb: idx < named.length - 1 ? 2 : 0 }}>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 600,
                fontFamily: "Work Sans, sans-serif",
                color: COLORS.GREY_900,
                mb: 0.5
              }}
            >
              {name} ·{" "}
              <Box
                component="span"
                sx={{ fontStyle: "italic", fontWeight: 500 }}
              >
                {pct}% accounted for
              </Box>
            </Typography>
            <DailyBarChart
              dates={dates}
              bars={bars}
              yMax={yMax}
              yTicks={yTicks}
              formatTick={(t) => String(t)}
              height={60}
              todayDate={todayDate}
              yLabel="# doses"
            />
          </Box>
        );
      })}
      {series.some((s) => !s.autologOnly) && <Legend />}
    </Box>
  );
}
